import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { initFirestorter, getFirebaseApp } from 'firestorter';
import * as Sentry from '@sentry/browser';

import User from '../../../Model/User';
import config from '../../config';
import useComponentMounted from '../../hooks/useComponentMounted';
import localFirebase from '../../firebase';
import FirebaseContext from './FirebaseContext';

const FirebaseContextProvider = ({
  firebase,
  children,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      const user = firebase.auth.currentUser;
      if (!user) {
        await firebase.auth.signInWithEmailAndPassword(config.testAuthUser.email, config.testAuthUser.password);
      }

      try {
        getFirebaseApp();
      } catch (error) {
        initFirestorter({ firebase: firebase.app });
      }
      const authUserDoc = await User.getById(firebase.auth.currentUser.uid);

      // The chat reads the signed-in user as soon as it is ready, so without one it stays on its spinner.
      if (!authUserDoc) {
        Sentry.captureMessage('Signed-in chat user not found', { extra: { userId: firebase.auth.currentUser.uid } });
        return;
      }

      if (isComponentMountedRef.current) {
        setAuthUser(authUserDoc);
        setIsReady(true);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    isReady,
    firebase,
    isComponentMountedRef,
  ]);

  const context = useMemo(() => ({
    isReady,
    firebase,
    authUser,
  }), [
    isReady,
    firebase,
    authUser,
  ]);

  return (
    <FirebaseContext.Provider value={context}>
      {children}
    </FirebaseContext.Provider>
  );
};

FirebaseContextProvider.propTypes = {
  firebase: PropTypes.object,
  children: PropTypes.node.isRequired,
};

FirebaseContextProvider.defaultProps = {
  firebase: localFirebase,
};

export default FirebaseContextProvider;
